import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import config from "../config/config";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const TYPE_OPTIONS = [
  { value: "language_certificate", label: "Sprachnachweis" },
  { value: "education_certificate", label: "Bildungsnachweis" },
  { value: "work_reference", label: "Arbeitsnachweis" },
  { value: "professional_certificate", label: "Berufsnachweis" },
  { value: "other", label: "Weiteres" },
];

const CANDIDATE_STATUS_OPTIONS = [
  { value: "declared", label: "Eingetragen" },
  { value: "pending_review", label: "Zur KI-Pruefung bereit" },
];

const CEFR_OPTIONS = ["A1", "A2", "B1", "B2", "C1", "C2"];
const PLATFORM_STATUSES = new Set(["formally_checked", "rejected"]);

function createEmptyReference() {
  return {
    type: "language_certificate",
    title: "",
    issuer: "",
    source: "",
    date_from: "",
    date_to: "",
    issued_at: "",
    reference_no: "",
    reference_text: "",
    file_path: "",
    status: "declared",
    notes: "",
    certificate_language: "",
    claimed_cefr_level: "",
    issuing_body: "",
    provider_name: "",
    exam_date: "",
    certificate_reference_no: "",
    exam_country: "",
    exam_center: "",
  };
}

function statusClass(status) {
  const value = String(status || "").toLowerCase();
  if (value === "formally_checked") return "is-checked";
  if (value === "pending_review") return "is-pending";
  if (value === "rejected") return "is-rejected";
  if (value === "uploaded") return "is-uploaded";
  return "is-default";
}

function isPlatformManagedStatus(status) {
  return PLATFORM_STATUSES.has(String(status || "").toLowerCase());
}

function isServerFilePath(value) {
  return /^\/uploads\/verification_refs\/[A-Za-z0-9._-]+$/.test(String(value || "").trim());
}

function getProtectedFileLabel(value) {
  const text = trimString(value);
  if (!isServerFilePath(text)) return "";
  const parts = text.split("/");
  return parts[parts.length - 1] || "";
}

function trimString(value) {
  return String(value || "").trim();
}

function isLanguageCertificateType(type) {
  return trimString(type) === "language_certificate";
}

function isValidIsoDate(value) {
  const text = trimString(value);
  if (!text) return false;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(text)) return false;
  const parsed = new Date(`${text}T00:00:00Z`);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === text;
}

function buildLanguageCertificateTitle(entry) {
  const language = trimString(entry.certificate_language);
  const level = trimString(entry.claimed_cefr_level).toUpperCase();
  const parts = [language ? `Sprachzertifikat ${language}` : "Sprachzertifikat", level || ""].filter(Boolean);
  return parts.join(" ").trim();
}

function normalizeEntryForSave(entry, options = {}) {
  const languageCertificate = isLanguageCertificateType(entry.type);
  const certificateLanguage = trimString(entry.certificate_language);
  const claimedCefrLevel = trimString(entry.claimed_cefr_level).toUpperCase();
  const issuingBody = trimString(entry.issuing_body);
  const providerName = trimString(entry.provider_name);
  const examDate = trimString(entry.exam_date);
  const certificateReferenceNo = trimString(entry.certificate_reference_no);
  const derivedTitle = buildLanguageCertificateTitle(entry);

  const payload = {
    type: trimString(entry.type),
    title: languageCertificate ? derivedTitle : trimString(entry.title),
    issuer: languageCertificate ? issuingBody : trimString(entry.issuer),
    source: languageCertificate ? providerName : trimString(entry.source),
    date_from: languageCertificate ? "" : trimString(entry.date_from),
    date_to: languageCertificate ? "" : trimString(entry.date_to),
    issued_at: languageCertificate ? examDate : trimString(entry.issued_at),
    reference_no: languageCertificate ? certificateReferenceNo : trimString(entry.reference_no),
    reference_text: trimString(entry.reference_text),
    file_path: trimString(entry.file_path),
    notes: trimString(entry.notes),
    certificate_language: languageCertificate ? certificateLanguage : "",
    claimed_cefr_level: languageCertificate ? claimedCefrLevel : "",
    issuing_body: languageCertificate ? issuingBody : "",
    provider_name: languageCertificate ? providerName : "",
    exam_date: languageCertificate ? examDate : "",
    certificate_reference_no: languageCertificate ? certificateReferenceNo : "",
    exam_country: languageCertificate ? trimString(entry.exam_country) : "",
    exam_center: languageCertificate ? trimString(entry.exam_center) : "",
  };

  if (!options.preservePlatformStatus) {
    payload.status = trimString(entry.status) || "declared";
  }

  return payload;
}

function validateEntry(entry) {
  const isLanguageCertificate = isLanguageCertificateType(entry.type);
  const title = trimString(entry.title);
  const issuer = trimString(entry.issuer);
  const source = trimString(entry.source);
  const referenceNo = trimString(entry.reference_no);
  const referenceText = trimString(entry.reference_text);
  const filePath = trimString(entry.file_path);
  const dateFrom = trimString(entry.date_from);
  const dateTo = trimString(entry.date_to);
  const issuedAt = trimString(entry.issued_at);
  const certificateLanguage = trimString(entry.certificate_language);
  const claimedCefrLevel = trimString(entry.claimed_cefr_level).toUpperCase();
  const issuingBody = trimString(entry.issuing_body);
  const providerName = trimString(entry.provider_name);
  const examDate = trimString(entry.exam_date);

  if (isLanguageCertificate) {
    if (!certificateLanguage) return "Bitte trage die Zertifikatssprache ein.";
    if (!CEFR_OPTIONS.includes(claimedCefrLevel)) return "Bitte waehle ein gueltiges CEFR-Level.";
    if (!examDate || !isValidIsoDate(examDate)) return "Bitte gib ein gueltiges Pruefungsdatum an.";
    if (!issuingBody && !providerName) return "Bitte trage Aussteller oder Anbieter ein.";
    if (filePath && !isServerFilePath(filePath)) {
      return "Dateien muessen aus dem sicheren Uploadpfad kommen.";
    }
    return "";
  }

  if (!title) return "Ein Titel ist noetig.";
  if (!dateFrom && !dateTo && !issuedAt) return "Bitte gib mindestens ein Datum an.";
  if (dateFrom && dateTo && dateFrom > dateTo) return "Der Start muss vor dem Enddatum liegen.";
  if (issuedAt && dateTo && issuedAt < dateTo) return "Das Ausstellungsdatum kann nicht vor dem Enddatum liegen.";
  if (issuedAt && dateFrom && issuedAt < dateFrom) return "Das Ausstellungsdatum kann nicht vor dem Start liegen.";
  if (!issuer && !source && !referenceNo && !referenceText && !filePath) {
    return "Fuege Aussteller, Quelle, Referenztext, Referenznummer oder eine Datei hinzu.";
  }
  if (filePath && !isServerFilePath(filePath)) {
    return "Dateien muessen aus dem sicheren Uploadpfad kommen.";
  }
  return "";
}

function candidateStatusValue(status) {
  return String(status || "").toLowerCase() === "pending_review" ? "pending_review" : "declared";
}

function LanguageCertificateFields({ entry, onChange }) {
  return (
    <>
      <div>
        <Label className="applicant-workspace-label">Zertifikatssprache</Label>
        <Input value={entry.certificate_language || ""} onChange={(e) => onChange("certificate_language", e.target.value)} className="applicant-field" />
      </div>
      <div>
        <Label className="applicant-workspace-label">CEFR-Level</Label>
        <Select value={entry.claimed_cefr_level || ""} onValueChange={(value) => onChange("claimed_cefr_level", value)}>
          <SelectTrigger className="applicant-field">
            <SelectValue placeholder="Level waehlen" />
          </SelectTrigger>
          <SelectContent>
            {CEFR_OPTIONS.map((option) => (
              <SelectItem key={option} value={option}>{option}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="applicant-workspace-label">Ausstellende Stelle</Label>
        <Input value={entry.issuing_body || ""} onChange={(e) => onChange("issuing_body", e.target.value)} className="applicant-field" />
      </div>
      <div>
        <Label className="applicant-workspace-label">Anbieter</Label>
        <Input value={entry.provider_name || ""} onChange={(e) => onChange("provider_name", e.target.value)} className="applicant-field" />
      </div>
      <div>
        <Label className="applicant-workspace-label">Pruefungsdatum</Label>
        <Input type="date" value={entry.exam_date || ""} onChange={(e) => onChange("exam_date", e.target.value)} className="applicant-field" />
      </div>
      <div>
        <Label className="applicant-workspace-label">Zertifikatsnummer</Label>
        <Input value={entry.certificate_reference_no || ""} onChange={(e) => onChange("certificate_reference_no", e.target.value)} className="applicant-field" />
      </div>
      <div>
        <Label className="applicant-workspace-label">Pruefungsland</Label>
        <Input value={entry.exam_country || ""} onChange={(e) => onChange("exam_country", e.target.value)} className="applicant-field" />
      </div>
      <div>
        <Label className="applicant-workspace-label">Pruefungszentrum</Label>
        <Input value={entry.exam_center || ""} onChange={(e) => onChange("exam_center", e.target.value)} className="applicant-field" />
      </div>
    </>
  );
}

function prettifyToken(value) {
  const text = trimString(value);
  if (!text) return "";
  return text
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function displayAiValue(value) {
  if (value === null || value === undefined) return "";
  if (Array.isArray(value)) {
    return value.map(displayAiValue).filter(Boolean).join(", ");
  }
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  if (typeof value === "boolean") return value ? "Ja" : "Nein";
  if (typeof value === "object") return "";
  return trimString(value);
}

function maybeFormatDate(value) {
  const text = displayAiValue(value);
  if (!text) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) {
    const parsed = new Date(`${text}T00:00:00Z`);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toLocaleDateString("de-DE");
    }
  }
  return text;
}

function readObjectPath(source, path) {
  if (!source || typeof source !== "object") return undefined;
  return String(path || "")
    .split(".")
    .filter(Boolean)
    .reduce((acc, key) => (acc && typeof acc === "object" ? acc[key] : undefined), source);
}

function pickExtractedValue(source, paths = []) {
  for (const path of paths) {
    const value = readObjectPath(source, path);
    const formatted = displayAiValue(value);
    if (formatted) return formatted;
  }
  return "";
}

function buildAiKeyFacts(result) {
  const extracted = result?.extracted_json && typeof result.extracted_json === "object"
    ? result.extracted_json
    : {};
  const detectedType = trimString(result?.document_type_detected);

  const candidates = [
    { label: "Aussteller", value: pickExtractedValue(extracted, ["issuing_body", "issuer", "issuing_authority", "provider", "provider_name"]) },
    { label: "Institution", value: pickExtractedValue(extracted, ["institution_name", "institution", "school_name", "university_name"]) },
    { label: "Arbeitgeber", value: pickExtractedValue(extracted, ["employer_name", "company_name", "organization_name"]) },
    { label: "Abschluss / Zertifikat", value: pickExtractedValue(extracted, ["certificate_name", "qualification_name", "degree_name", "document_title", "title"]) },
    { label: "Sprache", value: pickExtractedValue(extracted, ["certificate_language", "language", "document_language"]) },
    { label: "CEFR-Level", value: pickExtractedValue(extracted, ["claimed_cefr_level_visible", "visible_cefr_level", "cefr_level", "claimed_cefr_level"]) },
    { label: "Pruefungsdatum", value: maybeFormatDate(pickExtractedValue(extracted, ["exam_date", "issued_at", "issue_date"])) },
    { label: "Referenznummer", value: pickExtractedValue(extracted, ["certificate_reference_no", "reference_no", "document_number", "certificate_number"]) },
    { label: "Pruefungsland", value: pickExtractedValue(extracted, ["exam_country", "country"]) },
    { label: "Pruefungszentrum", value: pickExtractedValue(extracted, ["exam_center", "center_name", "exam_location"]) },
    { label: "Position / Rolle", value: pickExtractedValue(extracted, ["position_title", "job_title", "role_title"]) },
    { label: "Zeitraum", value: pickExtractedValue(extracted, ["employment_period", "study_period", "period"]) },
    { label: "Datum", value: maybeFormatDate(pickExtractedValue(extracted, ["document_date", "date", "graduation_date"])) },
  ];

  const preferredLabelsByType = {
    language_certificate: ["Aussteller", "Sprache", "CEFR-Level", "Pruefungsdatum", "Referenznummer", "Pruefungsland", "Pruefungszentrum"],
    work_reference: ["Arbeitgeber", "Position / Rolle", "Zeitraum", "Datum", "Referenznummer"],
    employment_reference: ["Arbeitgeber", "Position / Rolle", "Zeitraum", "Datum", "Referenznummer"],
    education_certificate: ["Institution", "Abschluss / Zertifikat", "Sprache", "Datum", "Referenznummer"],
    degree_certificate: ["Institution", "Abschluss / Zertifikat", "Datum", "Referenznummer"],
    training_certificate: ["Institution", "Abschluss / Zertifikat", "Datum", "Referenznummer"],
    professional_certificate: ["Aussteller", "Abschluss / Zertifikat", "Datum", "Referenznummer"],
    license_document: ["Aussteller", "Abschluss / Zertifikat", "Datum", "Referenznummer"],
  };

  const preferredLabels = preferredLabelsByType[detectedType] || candidates.map((item) => item.label);
  const facts = preferredLabels
    .map((label) => candidates.find((item) => item.label === label))
    .filter((item) => item && item.value);

  return facts.slice(0, 6);
}

function buildAiFlagFacts(result) {
  const flags = result?.flags_json && typeof result.flags_json === "object"
    ? result.flags_json
    : {};

  return [
    { label: "Lesbarkeit", value: displayAiValue(flags.readability) },
    { label: "Vollstaendigkeit", value: displayAiValue(flags.completeness) },
    { label: "Integritaet", value: displayAiValue(flags.integrity_assessment) },
  ].filter((item) => item.value);
}

function VerificationAiResultPanel({ result }) {
  if (!result) return null;

  const decisionLabel = result.decision === "accepted"
    ? "Automatisch akzeptiert"
    : result.decision === "rejected"
      ? "Automatisch abgelehnt"
      : "Noch keine Entscheidung";
  const decisionStatus = result.decision === "accepted"
    ? "formally_checked"
    : result.decision === "rejected"
      ? "rejected"
      : "pending_review";
  const keyFacts = buildAiKeyFacts(result);
  const flagFacts = buildAiFlagFacts(result);
  const readConfidence = result.read_confidence === null || result.read_confidence === undefined
    ? ""
    : `${Math.round(Number(result.read_confidence) * 100)} %`;
  const summaryText = trimString(result.summary_de);
  const translationText = trimString(result.translation_de);
  const reasonCode = trimString(result.reason_code);
  const reasonText = trimString(result.reason_text_de);
  const reuploadHint = trimString(result.reupload_hint_de);

  return (
    <div className="applicant-empty-card">
      <div className="applicant-tool-card__head applicant-tool-card__head--compact">
        <div>
          <span className="applicant-section-kicker">KI-Verifikation</span>
          <h4>{decisionLabel}</h4>
          <p className="applicant-feedback-text">
            Das Dokument wurde automatisiert gelesen, zusammengefasst und ohne Human Review bewertet.
          </p>
        </div>
        <div className="applicant-reference-actions">
          <span className={`applicant-status-badge ${statusClass(decisionStatus)}`}>
            {result.decision ? prettifyToken(result.decision) : "Keine Entscheidung"}
          </span>
          <span className={`applicant-status-badge ${statusClass(result.processing_status || "pending_review")}`}>
            {prettifyToken(result.processing_status || "unbekannt")}
          </span>
        </div>
      </div>
      <div className="applicant-reference-grid">
        <div>
          <Label className="applicant-workspace-label">Erkannter Typ</Label>
          <p className="applicant-feedback-text">{prettifyToken(result.document_type_detected) || "Noch nicht erkannt"}</p>
        </div>
        <div>
          <Label className="applicant-workspace-label">Dokumentenfamilie</Label>
          <p className="applicant-feedback-text">{prettifyToken(result.document_family) || "Noch nicht erkannt"}</p>
        </div>
        <div>
          <Label className="applicant-workspace-label">Quellsprache</Label>
          <p className="applicant-feedback-text">{result.source_language || "Unbekannt"}</p>
        </div>
        <div>
          <Label className="applicant-workspace-label">Verarbeitung</Label>
          <p className="applicant-feedback-text">{prettifyToken(result.processing_status) || "Noch nicht bewertet"}</p>
        </div>
        <div>
          <Label className="applicant-workspace-label">Lesestatus</Label>
          <p className="applicant-feedback-text">{prettifyToken(result.read_status) || "Noch nicht bewertet"}</p>
        </div>
        {readConfidence ? (
          <div>
            <Label className="applicant-workspace-label">Lesekonfidenz</Label>
            <p className="applicant-feedback-text">{readConfidence}</p>
          </div>
        ) : null}
      </div>
      {result.decision === "rejected" ? (
        <div className="applicant-workspace-field">
          <Label className="applicant-workspace-label">Re-Upload erforderlich</Label>
          <div className="applicant-feedback-text is-warning">
            {reasonCode ? <strong>{reasonCode}</strong> : null}
            {reasonText ? <span>{reasonText}</span> : null}
            {reuploadHint ? <span>{reuploadHint}</span> : null}
            <span>Du kannst dieses Dokument direkt im bestehenden Upload-Feld ersetzen und erneut speichern.</span>
          </div>
        </div>
      ) : null}
      {summaryText ? (
        <div className="applicant-workspace-field">
          <Label className="applicant-workspace-label">Summary</Label>
          <div className="applicant-empty-card">
            <p className="applicant-feedback-text">{summaryText}</p>
          </div>
        </div>
      ) : null}
      {keyFacts.length ? (
        <div className="applicant-workspace-field">
          <Label className="applicant-workspace-label">Wichtige Kerndaten</Label>
          <div className="applicant-reference-grid">
            {keyFacts.map((fact) => (
              <div key={fact.label}>
                <Label className="applicant-workspace-label">{fact.label}</Label>
                <p className="applicant-feedback-text">{fact.value}</p>
              </div>
            ))}
          </div>
        </div>
      ) : null}
      {flagFacts.length ? (
        <div className="applicant-workspace-field">
          <Label className="applicant-workspace-label">Pruefhinweise</Label>
          <div className="applicant-reference-grid">
            {flagFacts.map((fact) => (
              <div key={fact.label}>
                <Label className="applicant-workspace-label">{fact.label}</Label>
                <p className="applicant-feedback-text">{fact.value}</p>
              </div>
            ))}
          </div>
        </div>
      ) : null}
      {translationText ? (
        <div className="applicant-workspace-field">
          <Label className="applicant-workspace-label">KI-Uebersetzung</Label>
          <p className="applicant-feedback-text">Das Originaldokument bleibt fuehrend. Diese Uebersetzung ist ein KI-Artefakt zur schnelleren Einordnung.</p>
          <Textarea rows={8} value={translationText} readOnly className="applicant-field" />
        </div>
      ) : null}
      {result.processing_error ? (
        <p className="applicant-feedback-text is-error">{result.processing_error}</p>
      ) : null}
    </div>
  );
}

const CVVerificationReferencesBox = ({ userid }) => {
  const activeUserid = String(userid || localStorage.getItem("userid") || "").trim();
  const token = localStorage.getItem("token");

  const [rows, setRows] = useState([]);
  const [draft, setDraft] = useState(createEmptyReference());
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState("");
  const [uploadingId, setUploadingId] = useState("");
  const [processingId, setProcessingId] = useState("");
  const [openingDocumentId, setOpeningDocumentId] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const authHeaders = useMemo(() => {
    const headers = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    return headers;
  }, [token]);

  const loadRows = useCallback(async () => {
    if (!activeUserid) return;
    try {
      setLoading(true);
      setError("");
      const res = await axios.get(`${config.API_BASE_URL}/api/cv/verification-references`, {
        headers: authHeaders,
        params: { userid: activeUserid },
      });
      setRows(res.data?.rows || []);
    } catch (err) {
      setError(err.response?.data?.message || "Nachweise konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, [activeUserid, authHeaders]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const patchDraft = (key, value) => {
    setMessage("");
    setError("");
    setDraft((prev) => ({ ...prev, [key]: value }));
  };

  const patchRow = (id, key, value) => {
    setMessage("");
    setError("");
    setRows((prev) => prev.map((row) => (row.id === id ? { ...row, [key]: value } : row)));
  };

  const clearRowFile = (id) => patchRow(id, "file_path", "");
  const clearDraftFile = () => patchDraft("file_path", "");

  const createReference = async () => {
    if (!activeUserid) {
      setError("Keine Applicant-ID gefunden.");
      return;
    }

    const validationError = validateEntry(draft);
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setSavingId("draft");
      setMessage("");
      setError("");
      const res = await axios.post(
        `${config.API_BASE_URL}/api/cv/verification-references`,
        { userid: activeUserid, ...normalizeEntryForSave(draft) },
        { headers: authHeaders }
      );
      setRows((prev) => [res.data.reference, ...prev]);
      setDraft(createEmptyReference());
      if (res.data.processing_error) {
        setMessage("Nachweis angelegt.");
        setError(res.data.processing_error);
      } else {
        setMessage(res.data.reference?.ai_result ? "Nachweis angelegt und KI geprueft." : "Nachweis angelegt.");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Nachweis konnte nicht angelegt werden.");
    } finally {
      setSavingId("");
    }
  };

  const updateReference = async (row) => {
    if (!row?.id) return;

    const validationError = validateEntry(row);
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setSavingId(String(row.id));
      setMessage("");
      setError("");
      const res = await axios.put(
        `${config.API_BASE_URL}/api/cv/verification-references/${row.id}`,
        {
          userid: activeUserid,
          ...normalizeEntryForSave(row, { preservePlatformStatus: isPlatformManagedStatus(row.status) }),
        },
        { headers: authHeaders }
      );
      setRows((prev) => prev.map((entry) => (entry.id === row.id ? res.data.reference : entry)));
      if (res.data.processing_error) {
        setMessage("Nachweis aktualisiert.");
        setError(res.data.processing_error);
      } else {
        setMessage(res.data.reference?.ai_result ? "Nachweis aktualisiert und KI geprueft." : "Nachweis aktualisiert.");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Nachweis konnte nicht aktualisiert werden.");
    } finally {
      setSavingId("");
    }
  };

  const deleteReference = async (id) => {
    if (!id) return;
    try {
      setSavingId(String(id));
      setMessage("");
      setError("");
      await axios.delete(`${config.API_BASE_URL}/api/cv/verification-references/${id}`, {
        headers: authHeaders,
        params: { userid: activeUserid },
      });
      setRows((prev) => prev.filter((row) => row.id !== id));
      setMessage("Nachweis entfernt.");
    } catch (err) {
      setError(err.response?.data?.message || "Nachweis konnte nicht entfernt werden.");
    } finally {
      setSavingId("");
    }
  };

  const processReference = async (row) => {
    if (!row?.id || !row?.file_path) {
      setError("Bitte lade zuerst ein Dokument hoch.");
      return;
    }

    try {
      setProcessingId(String(row.id));
      setMessage("");
      setError("");
      const res = await axios.post(
        `${config.API_BASE_URL}/api/cv/verification-references/${row.id}/process-ai`,
        { userid: activeUserid },
        { headers: authHeaders }
      );
      setRows((prev) => prev.map((entry) => (entry.id === row.id ? res.data.reference : entry)));
      setMessage("Nachweis erneut durch die KI geprueft.");
    } catch (err) {
      const nextReference = err.response?.data?.reference;
      if (nextReference?.id) {
        setRows((prev) => prev.map((entry) => (entry.id === row.id ? nextReference : entry)));
      }
      setError(err.response?.data?.message || "KI-Pruefung konnte nicht gestartet werden.");
    } finally {
      setProcessingId("");
    }
  };

  const openProtectedDocument = async (row) => {
    if (!row?.id || !row?.file_path) {
      setError("Kein geschuetztes Dokument verfuegbar.");
      return;
    }

    try {
      setOpeningDocumentId(String(row.id));
      setMessage("");
      setError("");
      const res = await axios.get(
        `${config.API_BASE_URL}/api/cv/verification-references/${row.id}/document`,
        {
          headers: authHeaders,
          responseType: "blob",
        }
      );
      const contentType = res.headers["content-type"] || "application/octet-stream";
      const blob = new Blob([res.data], { type: contentType });
      const objectUrl = window.URL.createObjectURL(blob);
      const popup = window.open(objectUrl, "_blank", "noopener,noreferrer");
      if (!popup) {
        const fallback = document.createElement("a");
        fallback.href = objectUrl;
        fallback.target = "_blank";
        fallback.rel = "noopener noreferrer";
        fallback.click();
      }
      window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 60_000);
    } catch (err) {
      setError(err.response?.data?.message || "Dokument konnte nicht geoeffnet werden.");
    } finally {
      setOpeningDocumentId("");
    }
  };

  const uploadFile = async (targetId, file, onUploaded) => {
    if (!file) return;
    try {
      setUploadingId(targetId);
      setMessage("");
      setError("");
      const formData = new FormData();
      formData.append("userid", activeUserid);
      formData.append("file", file);

      const headers = { ...authHeaders, "Content-Type": "multipart/form-data" };
      const res = await axios.post(`${config.API_BASE_URL}/api/cv/verification-references/upload`, formData, { headers });
      onUploaded(res.data?.filePath || "");
      setMessage("Datei hochgeladen.");
    } catch (err) {
      setError(err.response?.data?.message || "Datei konnte nicht hochgeladen werden.");
    } finally {
      setUploadingId("");
    }
  };

  return (
    <div className="applicant-tool-card applicant-tool-card--references">
      <div className="applicant-tool-card__head">
        <div>
          <span className="applicant-section-kicker">Nachweise</span>
          <h4>Dokumente und Referenzen</h4>
        </div>
      </div>

      <div className="applicant-tool-card__intro">
        Trage hier Nachweise ein, die deine Bewerbung staerken. Dokumente werden automatisiert gelesen, zusammengefasst, bei Bedarf uebersetzt und formal akzeptiert oder mit Re-Upload-Hinweis abgelehnt.
      </div>

      <div className="applicant-tool-card__body applicant-tool-card__body--stack">
        <div className="applicant-reference-editor">
          <h4>Neuen Nachweis anlegen</h4>
          <div className="applicant-reference-grid">
            <div>
              <Label className="applicant-workspace-label">Typ</Label>
              <Select value={draft.type} onValueChange={(value) => patchDraft("type", value)}>
                <SelectTrigger className="applicant-field">
                  <SelectValue placeholder="Typ waehlen" />
                </SelectTrigger>
                <SelectContent>
                  {TYPE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="applicant-workspace-label">Dein Status</Label>
              <Select value={candidateStatusValue(draft.status)} onValueChange={(value) => patchDraft("status", value)}>
                <SelectTrigger className="applicant-field">
                  <SelectValue placeholder="Status waehlen" />
                </SelectTrigger>
                <SelectContent>
                  {CANDIDATE_STATUS_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {isLanguageCertificateType(draft.type) ? (
              <LanguageCertificateFields entry={draft} onChange={patchDraft} />
            ) : (
              <>
                <div>
                  <Label className="applicant-workspace-label">Titel</Label>
                  <Input value={draft.title} onChange={(e) => patchDraft("title", e.target.value)} className="applicant-field" />
                </div>
                <div>
                  <Label className="applicant-workspace-label">Aussteller</Label>
                  <Input value={draft.issuer} onChange={(e) => patchDraft("issuer", e.target.value)} className="applicant-field" />
                </div>
                <div>
                  <Label className="applicant-workspace-label">Quelle</Label>
                  <Input value={draft.source} onChange={(e) => patchDraft("source", e.target.value)} className="applicant-field" />
                </div>
                <div>
                  <Label className="applicant-workspace-label">Referenznummer</Label>
                  <Input value={draft.reference_no} onChange={(e) => patchDraft("reference_no", e.target.value)} className="applicant-field" />
                </div>
                <div>
                  <Label className="applicant-workspace-label">Ausgestellt am</Label>
                  <Input type="date" value={draft.issued_at || ""} onChange={(e) => patchDraft("issued_at", e.target.value)} className="applicant-field" />
                </div>
                <div className="applicant-reference-grid applicant-reference-grid--dates">
                  <div>
                    <Label className="applicant-workspace-label">Von</Label>
                    <Input type="date" value={draft.date_from || ""} onChange={(e) => patchDraft("date_from", e.target.value)} className="applicant-field" />
                  </div>
                  <div>
                    <Label className="applicant-workspace-label">Bis</Label>
                    <Input type="date" value={draft.date_to || ""} onChange={(e) => patchDraft("date_to", e.target.value)} className="applicant-field" />
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="applicant-workspace-field">
            <Label className="applicant-workspace-label">Kurzbeschreibung</Label>
            <Textarea rows={3} value={draft.reference_text} onChange={(e) => patchDraft("reference_text", e.target.value)} className="applicant-field" />
          </div>
          <div className="applicant-workspace-field">
            <Label className="applicant-workspace-label">Deine Notiz</Label>
            <Textarea rows={2} value={draft.notes} onChange={(e) => patchDraft("notes", e.target.value)} className="applicant-field" />
          </div>
          <div className="applicant-workspace-field">
            <Label className="applicant-workspace-label">Datei</Label>
            <Input
              value={getProtectedFileLabel(draft.file_path) || ""}
              readOnly
              placeholder="Noch keine Datei hochgeladen"
              className="applicant-field"
            />
            <div className="applicant-reference-actions">
              <Label className="applicant-chip-button applicant-chip-button-upload">
                {uploadingId === "draft" ? "Laedt hoch ..." : "Datei hochladen"}
                <input
                  type="file"
                  accept=".pdf,image/png,image/jpeg,image/webp"
                  className="hidden"
                  onChange={(e) => uploadFile("draft", e.target.files?.[0], (filePath) => patchDraft("file_path", filePath))}
                />
              </Label>
              {draft.file_path ? (
                <Button type="button" size="sm" variant="outline" className="applicant-chip-button applicant-chip-button-danger" onClick={clearDraftFile}>
                  Datei entfernen
                </Button>
              ) : null}
              <Button onClick={createReference} disabled={savingId === "draft"} className="applicant-cta-button">
                {savingId === "draft" ? "Speichert..." : "Nachweis anlegen"}
              </Button>
            </div>
          </div>
        </div>

        <div className="applicant-reference-stack">
          <div className="applicant-tool-card__head applicant-tool-card__head--compact">
            <div>
              <span className="applicant-section-kicker">Schon eingetragen</span>
              <h4>Deine Nachweise</h4>
            </div>
            <span className="applicant-count-pill">{rows.length}</span>
          </div>

          {loading ? <p className="applicant-feedback-text">Nachweise werden geladen ...</p> : null}

          {!loading && rows.length === 0 ? (
            <div className="applicant-empty-card">
              Noch kein Nachweis eingetragen.
            </div>
          ) : null}

          {rows.map((row) => {
            const platformManaged = isPlatformManagedStatus(row.status);
            return (
              <div key={row.id} className="applicant-reference-item">
                <div className="applicant-reference-item__head">
                  <div className="applicant-reference-item__status">
                    <span className={`applicant-status-badge ${statusClass(row.status)}`}>{row.status}</span>
                    <small>#{row.id}</small>
                  </div>
                  <div className="applicant-reference-actions">
                    <Button size="sm" variant="outline" className="applicant-chip-button" onClick={() => updateReference(row)} disabled={savingId === String(row.id)}>
                      {savingId === String(row.id) ? "Speichert..." : "Speichern"}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="applicant-chip-button"
                      onClick={() => processReference(row)}
                      disabled={processingId === String(row.id) || !row.file_path}
                    >
                      {processingId === String(row.id) ? "KI prueft..." : "KI erneut pruefen"}
                    </Button>
                    <Button size="sm" variant="outline" className="applicant-chip-button applicant-chip-button-danger" onClick={() => deleteReference(row.id)} disabled={savingId === String(row.id)}>
                      Loeschen
                    </Button>
                  </div>
                </div>

                {platformManaged ? (
                  <p className="applicant-feedback-text is-warning">
                    Der Plattform-Status ist schreibgeschuetzt. Wenn du Inhalte aenderst, geht der Nachweis wieder in deinen eigenen Bearbeitungsstand zurueck.
                  </p>
                ) : null}

                <div className="applicant-reference-grid">
                  <div>
                    <Label className="applicant-workspace-label">Typ</Label>
                    <Select value={row.type} onValueChange={(value) => patchRow(row.id, "type", value)}>
                      <SelectTrigger className="applicant-field">
                        <SelectValue placeholder="Typ waehlen" />
                      </SelectTrigger>
                      <SelectContent>
                        {TYPE_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="applicant-workspace-label">Dein Status</Label>
                    <Select value={candidateStatusValue(row.status)} onValueChange={(value) => patchRow(row.id, "status", value)}>
                      <SelectTrigger className="applicant-field">
                        <SelectValue placeholder="Status waehlen" />
                      </SelectTrigger>
                      <SelectContent>
                        {CANDIDATE_STATUS_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {isLanguageCertificateType(row.type) ? (
                    <LanguageCertificateFields entry={row} onChange={(key, value) => patchRow(row.id, key, value)} />
                  ) : (
                    <>
                      <div>
                        <Label className="applicant-workspace-label">Titel</Label>
                        <Input value={row.title || ""} onChange={(e) => patchRow(row.id, "title", e.target.value)} className="applicant-field" />
                      </div>
                      <div>
                        <Label className="applicant-workspace-label">Aussteller</Label>
                        <Input value={row.issuer || ""} onChange={(e) => patchRow(row.id, "issuer", e.target.value)} className="applicant-field" />
                      </div>
                      <div>
                        <Label className="applicant-workspace-label">Quelle</Label>
                        <Input value={row.source || ""} onChange={(e) => patchRow(row.id, "source", e.target.value)} className="applicant-field" />
                      </div>
                      <div>
                        <Label className="applicant-workspace-label">Referenznummer</Label>
                        <Input value={row.reference_no || ""} onChange={(e) => patchRow(row.id, "reference_no", e.target.value)} className="applicant-field" />
                      </div>
                      <div>
                        <Label className="applicant-workspace-label">Ausgestellt am</Label>
                        <Input type="date" value={row.issued_at || ""} onChange={(e) => patchRow(row.id, "issued_at", e.target.value)} className="applicant-field" />
                      </div>
                      <div className="applicant-reference-grid applicant-reference-grid--dates">
                        <div>
                          <Label className="applicant-workspace-label">Von</Label>
                          <Input type="date" value={row.date_from || ""} onChange={(e) => patchRow(row.id, "date_from", e.target.value)} className="applicant-field" />
                        </div>
                        <div>
                          <Label className="applicant-workspace-label">Bis</Label>
                          <Input type="date" value={row.date_to || ""} onChange={(e) => patchRow(row.id, "date_to", e.target.value)} className="applicant-field" />
                        </div>
                      </div>
                    </>
                  )}
                </div>

                <div className="applicant-workspace-field">
                  <Label className="applicant-workspace-label">Kurzbeschreibung</Label>
                  <Textarea rows={3} value={row.reference_text || ""} onChange={(e) => patchRow(row.id, "reference_text", e.target.value)} className="applicant-field" />
                </div>
                <div className="applicant-workspace-field">
                  <Label className="applicant-workspace-label">Deine Notiz</Label>
                  <Textarea rows={2} value={row.notes || ""} onChange={(e) => patchRow(row.id, "notes", e.target.value)} className="applicant-field" />
                </div>
                <div className="applicant-workspace-field">
                  <Label className="applicant-workspace-label">Datei</Label>
                  <Input
                    value={getProtectedFileLabel(row.file_path) || ""}
                    readOnly
                    placeholder="Noch keine Datei hochgeladen"
                    className="applicant-field"
                  />
                  <div className="applicant-reference-actions">
                    <Label className="applicant-chip-button applicant-chip-button-upload">
                      {uploadingId === String(row.id) ? "Laedt hoch ..." : "Datei hochladen"}
                      <input
                        type="file"
                        accept=".pdf,image/png,image/jpeg,image/webp"
                        className="hidden"
                        onChange={(e) => uploadFile(String(row.id), e.target.files?.[0], (filePath) => patchRow(row.id, "file_path", filePath))}
                      />
                    </Label>
                    {row.file_path ? (
                      <Button type="button" size="sm" variant="outline" className="applicant-chip-button applicant-chip-button-danger" onClick={() => clearRowFile(row.id)}>
                        Datei entfernen
                      </Button>
                    ) : null}
                    {row.file_path ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="applicant-chip-button"
                        onClick={() => openProtectedDocument(row)}
                        disabled={openingDocumentId === String(row.id)}
                      >
                        {openingDocumentId === String(row.id) ? "Oeffnet..." : "Dokument ansehen"}
                      </Button>
                    ) : null}
                  </div>
                </div>
                <VerificationAiResultPanel result={row.ai_result} />
              </div>
            );
          })}
        </div>

        {message ? <p className="applicant-feedback-text is-success">{message}</p> : null}
        {error ? <p className="applicant-feedback-text is-error">{error}</p> : null}
      </div>
    </div>
  );
};

export default CVVerificationReferencesBox;
